import { API_BASE_URL } from './api';

// ✅ Define the full API base for this module
const POLICY_API_URL = `${API_BASE_URL}/cancellation-policies`;

// ✅ Interface for type safety
interface Policy {
  cancellation_policy_id?: number;
  policy_time: string;
  hrs: number;
  charges_in_perc: number;
}

// ✅ Helper function to handle JSON + errors
async function handleResponse(response: Response) {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }

  // ✅ Ensure the return value is always an array
  if (Array.isArray(data.data)) {
    return data.data;
  } else if (Array.isArray(data)) {
    return data;
  } else {
    return []; // fallback in case data is not array
  }
}


// ✅ Get all cancellation policies
export const getCancellationPolicies = async (): Promise<Policy[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/cancellation-policies`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const result = await response.json();
    console.log('📦 Raw API result:', result);

    // ✅ Check all possible structures
    if (Array.isArray(result)) return result;
    if (Array.isArray(result.data)) return result.data;
    if (Array.isArray(result.data?.policies)) return result.data.policies;

    console.warn('⚠️ Unexpected API structure:', result);
    return [];
  } catch (error) {
    console.error('Error fetching cancellation policies:', error);
    throw error;
  }
};

// ✅ Get a single policy by ID
export const getCancellationPolicy = async (id: number): Promise<Policy> => {
  try {
    const response = await fetch(`${POLICY_API_URL}/${id}`, { method: 'GET' });
    return await handleResponse(response);
  } catch (error) {
    console.error(`Error fetching cancellation policy with ID ${id}:`, error);
    throw error;
  }
};

// ✅ Create a new cancellation policy
export const createCancellationPolicy = async (
  policy: Omit<Policy, 'cancellation_policy_id'>
): Promise<Policy> => {
  try {
    const response = await fetch(POLICY_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(policy),
    });
    return await handleResponse(response);
  } catch (error) {
    console.error('Error creating cancellation policy:', error);
    throw error;
  }
};

// ✅ Update an existing policy
export const updateCancellationPolicy = async (
  id: number,
  policy: Partial<Policy>
): Promise<Policy> => {
  try {
    const response = await fetch(`${POLICY_API_URL}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(policy),
    });
    return await handleResponse(response);
  } catch (error) {
    console.error(`Error updating cancellation policy with ID ${id}:`, error);
    throw error;
  }
};

// ✅ Delete a policy
export const deleteCancellationPolicy = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${POLICY_API_URL}/${id}`, { method: 'DELETE' });
    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.message || 'Failed to delete policy');
    }
  } catch (error) {
    console.error(`Error deleting cancellation policy with ID ${id}:`, error);
    throw error;
  }
};

// ✅ Default export for convenience
export default {
  getCancellationPolicies,
  getCancellationPolicy,
  createCancellationPolicy,
  updateCancellationPolicy,
  deleteCancellationPolicy,
};
